import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  returnAsset,
  decommissionAsset,
  reassignAsset,
  updateAssetCondition,
  logAssetService,
  updateAssetDetails,
} from '../../api/assets';
import { listSites } from '../../api/sites';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { Asset } from '../../types/api';

export type AssetActionKind = 'return' | 'decommission' | 'reassign' | 'condition' | 'service' | 'details';

const TITLES: Record<AssetActionKind, string> = {
  return: 'Return asset',
  decommission: 'Decommission asset',
  reassign: 'Reassign asset',
  condition: 'Update condition',
  service: 'Log a repair/service',
  details: 'Edit asset details',
};

export function AssetActionModal({
  asset,
  kind,
  open,
  onClose,
}: {
  asset: Asset;
  kind: AssetActionKind;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [notes, setNotes] = useState('');
  const [condition, setCondition] = useState('');
  const [toSiteId, setToSiteId] = useState('');
  // "service" kind - repair/maintenance history entry.
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  // "details" kind - correcting/updating warranty end date or serial number.
  const [warrantyEndDate, setWarrantyEndDate] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [termMonths, setTermMonths] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || kind !== 'details') return;
    setWarrantyEndDate(asset.warrantyEndDate ? asset.warrantyEndDate.slice(0, 10) : '');
    setSerialNumber(asset.serialNumber ?? '');
    setPurchaseDate(asset.purchaseDate ? asset.purchaseDate.slice(0, 10) : '');
    setPurchasePrice(asset.purchasePrice ?? '');
    setTermMonths(asset.termMonths ? String(asset.termMonths) : '');
  }, [open, kind, asset]);

  const orgId = asset.currentSite?.customerOrganisationId;
  const sitesQuery = useQuery({
    queryKey: ['sites', orgId],
    queryFn: () => listSites(orgId!),
    enabled: open && kind === 'reassign' && !!orgId,
  });

  const mutation = useMutation({
    mutationFn: () => {
      switch (kind) {
        case 'return':
          return returnAsset(asset.id, notes || undefined);
        case 'decommission':
          return decommissionAsset(asset.id, notes || undefined);
        case 'reassign':
          return reassignAsset(asset.id, { toSiteId: toSiteId || undefined, notes: notes || undefined });
        case 'condition':
          return updateAssetCondition(asset.id, condition, notes || undefined);
        case 'service':
          return logAssetService(asset.id, {
            description,
            cost: cost.trim() ? Number(cost) : undefined,
            vendor: vendor.trim() || undefined,
          });
        case 'details':
          return updateAssetDetails(asset.id, {
            warrantyEndDate: warrantyEndDate.trim() === '' ? null : warrantyEndDate,
            serialNumber: serialNumber.trim() || undefined,
            purchaseDate: purchaseDate.trim() === '' ? null : purchaseDate,
            purchasePrice: purchasePrice.trim() === '' ? null : Number(purchasePrice),
            termMonths: termMonths.trim() === '' ? null : Number(termMonths),
          });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', asset.id] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      showToast('Asset updated.');
      setNotes('');
      setCondition('');
      setToSiteId('');
      setDescription('');
      setCost('');
      setVendor('');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update the asset.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (kind === 'condition' && !condition.trim()) {
      setError('Condition is required.');
      return;
    }
    if (kind === 'reassign' && !toSiteId) {
      setError('Choose a site to reassign to.');
      return;
    }
    if (kind === 'service' && !description.trim()) {
      setError('Describe what was done.');
      return;
    }
    if (kind === 'service' && cost.trim() && (Number.isNaN(Number(cost)) || Number(cost) < 0)) {
      setError('Cost must be a positive number.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={TITLES[kind]}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="asset-action-form"
            type="submit"
            variant={kind === 'decommission' ? 'danger' : 'primary'}
            isLoading={mutation.isPending}
          >
            Confirm
          </Button>
        </>
      }
    >
      <form id="asset-action-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        {kind === 'condition' && (
          <Input
            label="Condition"
            required
            autoFocus
            placeholder="e.g. Faulty screen"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          />
        )}

        {kind === 'reassign' && (
          <Select label="Reassign to site" value={toSiteId} onChange={(e) => setToSiteId(e.target.value)}>
            <option value="">Select a site...</option>
            {sitesQuery.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}

        {kind === 'service' && (
          <>
            <Input
              label="What was done"
              required
              autoFocus
              placeholder="e.g. Replaced faulty screen"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Cost (£)" type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} />
              <Input label="Vendor" placeholder="e.g. Acme Repairs" value={vendor} onChange={(e) => setVendor(e.target.value)} />
            </div>
          </>
        )}

        {kind === 'details' && (
          <>
            <Input
              label="Warranty end date"
              type="date"
              hint="Leave blank to clear it."
              value={warrantyEndDate}
              onChange={(e) => setWarrantyEndDate(e.target.value)}
            />
            <Input label="Serial number" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Purchase date"
                type="date"
                hint="Leave blank to clear it."
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
              <Input
                label="Purchase price (£)"
                type="number"
                step="0.01"
                min="0"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
              />
            </div>
            <Input
              label="Term (months)"
              type="number"
              min="1"
              hint="How many months this asset is contracted/managed for."
              value={termMonths}
              onChange={(e) => setTermMonths(e.target.value)}
            />
          </>
        )}

        {kind !== 'service' && kind !== 'details' && (
          <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </form>
    </Modal>
  );
}
